import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';

type Bullet = { titulo?: string; condicao?: string; preco?: string };
type Offer = { id: string; month: string; title: string; content: string; parsed_bullets: Bullet[] | Record<string, unknown> };

export default function Ofertas() {
  const [offer, setOffer] = useState<Offer | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [flags, setFlags] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [bullets, setBullets] = useState<Bullet[]>([]);
  const [demo, setDemo] = useState(false);

  const load = useCallback(async () => {
    const d = await api.get<{ offer: Offer | null; demo: boolean }>('/offers/current');
    setOffer(d.offer);
    setDemo(d.demo);
    if (d.offer) {
      setTitle(d.offer.title);
      setContent(d.offer.content);
      const pb = d.offer.parsed_bullets;
      setBullets(Array.isArray(pb) && pb.length ? (pb as Bullet[]) : []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const month = new Date().toISOString().slice(0, 7);
  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.post('/offers', { month, title, content });
      toast.success('Oferta do mês salva.');
      await load();
    } catch (err) {
      const e = err as { message?: string; details?: Record<string, string[]> };
      const first = e.details ? Object.entries(e.details)[0] : null;
      setFormError(first ? `${first[0]}: ${first[1][0]}` : e.message ?? 'Falha ao salvar');
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const file = fd.get('offer') as File | null;
    if (!file || file.size === 0) { toast.error('Escolha o arquivo da oferta (zip/planilha/doc/pdf/txt).'); return; }
    setBusy(true);
    setFormError(null);
    try {
      const r = await api.multipart<{ offer: Offer; route: string; demo: boolean; flags: string[]; bullets: number; month: string }>('/offers/upload', fd);
      toast.success(r.demo
        ? 'Oferta lida em MODO DEMO (sem chave de IA configurada) — exemplo marcado, não é oferta real.'
        : `Material lido (${r.route}): ${r.bullets} ofertas estruturadas${r.flags.length ? ` · ${r.flags.length} avisos de verificação` : ''}.`);
      setFlags(r.flags ?? []);
      setBusy(false);
      await load();
    } catch (err) {
      setFormError((err as { message?: string }).message ?? 'Falha ao ler a oferta');
      setBusy(false);
    }
  }

  async function onRemove() {
    if (!offer) return;
    if (!confirm('Remover esta oferta do mês? O funil volta a não ter oferta vigente.')) return;
    setBusy(true);
    try {
      await api.delete(`/offers/${offer.id}`);
      toast.success('Oferta removida.');
      setOffer(null); setTitle(''); setContent(''); setBullets([]); setFlags([]);
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao remover');
    } finally {
      setBusy(false);
    }
  }

  async function onParse() {
    if (!offer) return;
    setBusy(true);
    try {
      const r = await api.post<{ bullets: Bullet[]; demo: boolean }>(`/offers/${offer.id}/parse`);
      setBullets(r.bullets ?? []);
      toast.success(r.demo ? 'Interpretação demo (sem chave de IA configurada).' : 'Ofertas interpretadas pela IA.');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha na interpretação');
    } finally {
      setBusy(false);
    }
  }

  const errorBanner = formError && (
    <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
      <p className="font-medium">Não foi possível concluir.</p>
      <p className="mt-1">{formError}</p>
      {formError.toLowerCase().includes('chave') && (
        <p className="mt-2">
          <a href="/painel/configuracoes" className="font-medium underline hover:no-underline">Abrir Configurações → Integrações</a>
        </p>
      )}
      <button type="button" onClick={() => setFormError(null)}
        className="mt-2 rounded-lg border border-red-300 px-3 py-1 text-xs font-medium hover:bg-red-100">Fechar</button>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Vigente</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Ofertas do mês ({month})</h1>
        {demo && <p className="mt-1 text-xs text-[var(--text-dim)]">Modo demo: sem chave de IA as respostas são exemplos.</p>}
      </header>
      <form onSubmit={onUpload} className="mt-4 space-y-3 rounded-lg border border-[var(--border)] bg-white p-4">
        <h2 className="text-sm font-semibold">Subir material real de ofertas</h2>
        <p className="text-xs text-[var(--text-dim)]">Qualquer formato que a Claro mandar: zip, planilha, doc, pdf, txt ou IMAGEM (print via IA — exige chave configurada). O texto é extraído FIEL (nada inventado), a IA estrutura e números questionados vêm marcados.</p>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Arquivo do material</span>
          <input name="offer" type="file" accept=".zip,.xlsx,.docx,.pdf,.txt,.csv,.png,.jpg,.jpeg,.webp"
            className="w-full text-sm text-[var(--text-dim)] file:mr-3 file:h-10 file:cursor-pointer file:rounded-lg file:border-0 file:bg-[var(--bg-subtle)] file:px-4 file:text-sm file:font-medium file:text-[var(--text)] hover:file:bg-[var(--border)]" />
        </label>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Mês (opcional; default o vigente)</span>
            <input name="month" placeholder="AAAA-MM" inputMode="numeric" className="h-10 w-32 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
          </label>
          <button type="submit" disabled={busy} className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
            {busy ? 'Lendo material...' : 'Subir e estruturar'}
          </button>
        </div>
        {errorBanner}
        {flags.length > 0 && (
          <ul className="space-y-1 rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] p-3 text-xs text-[var(--text-dim)]">
            {flags.map((fl) => <li key={fl}>⚠ {fl}</li>)}
          </ul>
        )}
      </form>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer text-sm text-[var(--text-dim)] hover:text-[var(--text)]">Alternativa: salvar/corrigir a oferta colando o texto</summary>
      <form onSubmit={onSave} className="mt-4 space-y-3">
        {errorBanner}
        <div>
          <label htmlFor="of-title" className="mb-1 block text-sm font-medium">Título</label>
          <input id="of-title" value={title} onChange={(e) => setTitle(e.target.value)} required
            className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
        </div>
        <div>
          <label htmlFor="of-content" className="mb-1 block text-sm font-medium">Detalhamento (texto cru)</label>
          <textarea id="of-content" value={content} onChange={(e) => setContent(e.target.value)} rows={8} required
            placeholder="Cole aqui as ofertas do mês da Claro Empresas (condições, preços, prazos...)"
            className="w-full rounded-lg border border-[var(--border-strong)] bg-white p-3 text-sm" />
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={busy} className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">Salvar oferta</button>
          {offer && (
            <>
              <button type="button" onClick={onParse} disabled={busy} className="h-10 rounded-lg border border-[var(--border-strong)] px-4 text-sm font-medium hover:bg-[var(--bg-subtle)] disabled:opacity-50">Interpretar com IA</button>
              <button type="button" onClick={onRemove} disabled={busy} className="h-10 rounded-lg border border-[var(--border-strong)] px-4 text-sm font-medium text-red-claro hover:bg-[var(--bg-subtle)] disabled:opacity-50">Remover oferta</button>
            </>
          )}
        </div>
      </form>
      </details>
      {bullets.length > 0 && (
        <section className="mt-6 rounded-lg border border-[var(--border)] bg-white p-4">
          <h2 className="text-sm font-semibold">Ofertas interpretadas</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {bullets.map((b, i) => (
              <li key={i} className="border-b border-[var(--border)] pb-2 last:border-0 last:pb-0">
                <p className="font-medium">{b.titulo ?? 'Oferta'}</p>
                <p className="text-[var(--text-dim)]">{[b.condicao, b.preco].filter(Boolean).join(' · ')}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
